var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { FormControlMixin } from "@open-wc/form-control";
import { html, LitElement } from "lit";
import { property, query } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";
import { buttonGroupComponentVariables } from "../button-group/styles";
import { reset } from "../styles";
import { styles } from "./styles";
/**
 * Checkboxes allow users to select one or more options from a number of choices.
 *
 * Wrap individual checkboxes in a checkbox group.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/checkbox/frameworks/elements)
 *
 * @parent w-checkbox-group
 */
export class WarpCheckbox extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        /**
         * The value submitted when the checkbox is checked.
         *
         * If no value attribute is set, the checkbox defaults to `on`. Unchecked and disabled checkboxes do not submit a value.
         */
        this.value = null;
        /**
         * Whether the checkbox is visually indeterminate.
         *
         * Use this for parent options that represent a mixed set of child selections. Clicking the checkbox clears the indeterminate state and sets the checkbox to checked.
         */
        this.indeterminate = false;
        /**
         * Whether the checkbox is checked.
         *
         * Checked checkboxes submit their value with form data. The property is reflected to the `checked` attribute.
         */
        this.checked = false;
        /**
         * Whether the checkbox is disabled.
         *
         * Disabled checkboxes cannot be focused, changed, or submitted with form data.
         */
        this.disabled = false;
        /**
         * Whether the checkbox must be checked before form submission.
         *
         * A required checkbox is invalid until it is checked. For requiring at least one option in a set, use `required` on `w-checkbox-group`.
         */
        this.required = false;
        /**
         * Whether the checkbox is visually invalid.
         *
         * Use this to show an externally managed validation error. Required validation also sets the invalid state after the user interacts with the checkbox or tries to submit the form.
         */
        this.invalid = false;
        /** The default value of the form control. Used for resetting. */
        this.#defaultChecked = false;
        // Track whether invalid state was set by required validation.
        this.#invalidFromRequired = false;
        // Track whether the user has interacted with the checkbox.
        this.#hasInteracted = false;
        /* @internal */
        this.#handleInvalid = () => {
            this.#markInteracted();
            this.#updateValidity();
        };
        /* @internal */
        this.#handleKeyDown = (event) => {
            if (this.disabled)
                return;
            if (event.defaultPrevented)
                return;
            if (event.key !== " " && event.key !== "Spacebar" && event.key !== "Enter")
                return;
            if (event.key === "Enter" && this.internals.form) {
                this.internals.form.requestSubmit();
                return;
            }
            if (event.composedPath()[0] === this.input)
                return;
            event.preventDefault();
            this.click();
        };
    }
    static { this.styles = [reset, buttonGroupComponentVariables, styles]; }
    static { this.shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    }; }
    /**
     * The name of the checkbox.
     *
     * When the checkbox is checked and belongs to a form, this name is submitted with the checkbox value. If the checkbox is inside a `w-checkbox-group` with a name, the group name is used when the checkbox does not have its own name.
     */
    set name(value) {
        this._ownName = value;
    }
    get name() {
        return this._ownName || this._groupName;
    }
    /** The default value of the form control. Used for resetting. */
    #defaultChecked;
    // Track whether invalid state was set by required validation.
    #invalidFromRequired;
    // Track whether the user has interacted with the checkbox.
    #hasInteracted;
    /** Computed invalid state: combines own invalid with group invalid */
    get _computedInvalid() {
        return this.invalid || this._groupInvalid === true;
    }
    connectedCallback() {
        super.connectedCallback();
        const attrValue = this.getAttribute("value");
        this.value = attrValue ?? "on";
        this.#defaultChecked = this.hasAttribute("checked");
        this.checked = this.#defaultChecked;
        this.addEventListener("invalid", this.#handleInvalid);
        this.addEventListener("keydown", this.#handleKeyDown);
        this.#syncFormValue();
    }
    /* @internal */
    handleClick() {
        if (this.disabled)
            return;
        this.#markInteracted();
        this.checked = !this.checked;
        this.indeterminate = false;
        this.updateComplete.then(() => {
            this.input.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
        });
    }
    /* @internal */
    #handleInvalid;
    /* @internal */
    #handleKeyDown;
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has("checked") ||
            changedProperties.has("indeterminate")) {
            this.#syncInputState();
        }
        if (this.#shouldSyncFormState(changedProperties)) {
            this.#syncFormValue();
            this.#updateValidity();
        }
    }
    resetFormControl() {
        this.checked = this.#defaultChecked;
        this.#syncFormValue();
        this.#updateValidity();
    }
    /* @internal */
    click() {
        if (this.disabled)
            return;
        this.input?.click();
    }
    /* @internal */
    focus(options) {
        this.input?.focus(options);
    }
    /* @internal */
    blur() {
        this.input?.blur();
    }
    /** Returns the validation message if the checkbox is invalid, otherwise an empty string */
    get validationMessage() {
        return this.internals.validationMessage;
    }
    /** Returns the validity state of the checkbox */
    get validity() {
        return this.internals.validity;
    }
    /** Checks whether the checkbox passes constraint validation */
    checkValidity() {
        this.#updateValidity();
        return this.internals.checkValidity();
    }
    /** Checks validity and shows the browser's validation message if invalid */
    reportValidity() {
        this.#markInteracted();
        this.#updateValidity();
        return this.internals.checkValidity();
    }
    /** @internal */
    #markInteracted() {
        this.#hasInteracted = true;
    }
    /** @internal */
    #getValidityMessage() {
        // Use a non-empty message to avoid native popovers while satisfying ElementInternals.
        return this.input?.validationMessage || " ";
    }
    /** @internal */
    #getValidityAnchor() {
        return this.input ?? undefined;
    }
    /** @internal */
    #setInvalidState(state) {
        this.internals.setValidity(state, this.#getValidityMessage(), this.#getValidityAnchor());
    }
    /** @internal */
    #updateValidity() {
        if (this.disabled) {
            this.internals.setValidity({});
            return;
        }
        const requiredInvalid = this.required && !this.checked;
        // this means that invalid was set via the invalid attribute, not by required validation
        const externalInvalid = this.invalid && !this.#invalidFromRequired;
        if (requiredInvalid) {
            this.#invalidFromRequired = true;
            this.invalid = this.#hasInteracted;
            this.#setInvalidState({ valueMissing: true });
            return;
        }
        if (this.#invalidFromRequired) {
            this.invalid = false;
            this.#invalidFromRequired = false;
        }
        if (externalInvalid) {
            this.#setInvalidState({ customError: true });
            return;
        }
        this.internals.setValidity({});
    }
    /** @internal */
    #syncFormValue() {
        if (this.disabled) {
            this.setValue(null);
            return;
        }
        const value = this.checked ? this.value : null;
        this.setValue(value);
    }
    /** @internal */
    #syncInputState() {
        if (!this.input)
            return;
        this.input.checked = this.checked;
        this.input.indeterminate = this.indeterminate;
    }
    /** @internal */
    #shouldSyncFormState(changedProperties) {
        return (changedProperties.has("checked") ||
            changedProperties.has("value") ||
            changedProperties.has("disabled") ||
            changedProperties.has("required") ||
            changedProperties.has("invalid"));
    }
    render() {
        const isIndeterminate = !this.checked && this.indeterminate;
        const ariaChecked = isIndeterminate
            ? "mixed"
            : this.checked
                ? "true"
                : "false";
        return html `
			<label part="base">
				<span part="control">
					<input
						part="input"
						type="checkbox"
						name=${ifDefined(this.name || undefined)}
						value=${ifDefined(this.value)}
						.indeterminate=${live(this.indeterminate)}
						.checked=${live(this.checked)}
						.disabled=${this.disabled}
						.required=${this.required}
						aria-checked=${ariaChecked}
						aria-invalid=${ifDefined(this._computedInvalid ? "true" : undefined)}
						@click=${this.handleClick}
					/>
					${isIndeterminate ? "–" : ""}
				</span>

				<slot part="label"></slot>
			</label>
		`;
    }
}
__decorate([
    query('input[type="checkbox"]')
], WarpCheckbox.prototype, "input", void 0);
__decorate([
    property({ reflect: true })
], WarpCheckbox.prototype, "name", null);
__decorate([
    property({ reflect: true })
], WarpCheckbox.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpCheckbox.prototype, "indeterminate", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpCheckbox.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpCheckbox.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpCheckbox.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpCheckbox.prototype, "invalid", void 0);
__decorate([
    property({ attribute: false })
], WarpCheckbox.prototype, "_groupInvalid", void 0);
__decorate([
    property({ attribute: false })
], WarpCheckbox.prototype, "_groupName", void 0);
/** @deprecated Use WarpCheckbox */
export const WCheckbox = WarpCheckbox;
if (!customElements.get("w-checkbox")) {
    customElements.define("w-checkbox", WarpCheckbox);
}
//# sourceMappingURL=checkbox.js.map